import React from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Footer from "../components/Footer";
import ShareButton from "../components/ShareButton";
import Faq from "../components/Faq";
import Icon, { SETTING_ICONS } from "../components/Icon";
import MeasurePrompt from "../components/MeasurePrompt";
import SplitPanel from "../components/SplitPanel";
import { NdGear } from "../components/GearRecommendation";
import { gradientForLighting } from "../utils/lightingGradients";
import { imageForLighting, altForLighting } from "../utils/lightingImages";
import { generateDelivery } from "../engine/deliveryEngine";
import { generateSkinToneGuidance } from "../engine/skinToneEngine";
import {
  frameRateSummary, profileSummary, apertureSummary,
  ndSummary, stabilizationSummary, coverageNote,
} from "../utils/cameraFacts";

/**
 * One settings row. The icon refers to the physical control, the label
 * names it, and the value is set in the data face so a column of them
 * aligns and scans like a readout.
 */
function SettingRow({ icon, label, children }) {
  return (
    <tr>
      <th scope="row">
        <span className="setting-label">
          <Icon name={SETTING_ICONS[icon] || "aperture"} />
          {label}
        </span>
      </th>
      <td className="setting-value data">{children}</td>
    </tr>
  );
}

function ConfidenceBadge({ label, tone, reasons }) {
  return (
    <div className={`confidence-badge confidence-${tone}`}>
      <span className="confidence-dot" aria-hidden="true" />
      <div>
        <strong>{label}</strong>
        {reasons && reasons.length > 0 && (
          <ul className="confidence-reasons">
            {reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function RecipePage({ camera, scenario, lighting, recipe, explanation, related = [], depth = null, output = null, slug }) {
  const { settings } = recipe;
  const delivery = generateDelivery(recipe, output, scenario);
  const skinTone = generateSkinToneGuidance(camera, lighting, settings, scenario);
  const grad = gradientForLighting(lighting.id);

  return (
    <>
    <SplitPanel
      gradient={grad}
      image={imageForLighting(lighting.id)}
      imageAlt={altForLighting(lighting.id, camera.name, scenario.name)}
      imageText={{
        eyebrow: camera.name,
        title: scenario.name,
        sub: lighting.name,
      }}
    >
    <article className="recipe-page split-article">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span>{" "}
        <Link to={`/cameras/${camera.id}`}>{camera.name}</Link>
      </nav>

      <header className="recipe-header">
        <div className="recipe-header-row">
          <h1>
            {camera.name} Settings for {scenario.name} in {lighting.name}
          </h1>
          {typeof window !== "undefined" && (
            <ShareButton
              title={`${camera.name}: ${scenario.name} Settings`}
              text={`${scenario.name} settings for the ${camera.name} in ${lighting.name.toLowerCase()}`}
              url={window.location.href}
              className="recipe-share-button"
            />
          )}
        </div>
        <p className="recipe-dek">{depth ? depth.opener : scenario.description}</p>
      </header>

      <ConfidenceBadge
        label={explanation.confidenceLabel}
        tone={explanation.confidenceTone}
        reasons={explanation.confidenceReasons}
      />

      {/* Single column, deliberately.
      
          An earlier version split this into a sticky settings column and a
          scrolling prose column. It sounded right — keep the numbers in
          view while reading about them — and it was wrong in practice:
          two columns inside a 900px page left the table cramped at 360px
          and the prose at a width nothing reads comfortably at. A settings
          table is short enough to remember; a squeezed one is hard enough
          to read that you stop trying. */}
      <section aria-labelledby="settings-heading">
        <h2 id="settings-heading">Recommended settings</h2>
        <div className="settings-section-row">
          <table className="settings-table">
            <tbody>
              <SettingRow icon="resolution" label="Resolution">{settings.resolution}</SettingRow>
              {settings.aspectRatio && (
                <SettingRow icon="aspectRatio" label="Aspect ratio">
                  {settings.aspectRatio} <span className="value-qualifier">({settings.outputName})</span>
                </SettingRow>
              )}
              <SettingRow icon="fps" label="Frame rate">{settings.fps}fps</SettingRow>
              <SettingRow icon="shutter" label="Shutter speed">{settings.shutter}</SettingRow>
              <SettingRow icon="iso" label="ISO">{settings.iso}</SettingRow>
              <SettingRow icon="whiteBalance" label="White balance">{settings.whiteBalance}</SettingRow>
              <SettingRow icon="colorTemperature" label="Colour temperature">{settings.colorTemperature}</SettingRow>
              <SettingRow icon="profile" label="Picture profile">{settings.profile}</SettingRow>
              <SettingRow icon="sharpness" label="Sharpness">{settings.sharpness}</SettingRow>
              <SettingRow icon="contrast" label="Contrast">{settings.contrast}</SettingRow>
              <SettingRow icon="saturation" label="Saturation">{settings.saturation}</SettingRow>
              <SettingRow icon="ndFilter" label="ND filter">{settings.ndFilter}</SettingRow>
              {settings.ndRequiredStops != null && (
                <SettingRow icon="ndStops" label="Light to remove">
                  {settings.ndRequiredStops} stops <span className="value-qualifier">over at this shutter and ISO</span>
                </SettingRow>
              )}
              <SettingRow icon="stabilization" label="Stabilization">{settings.stabilization}</SettingRow>
              <SettingRow icon="focus" label="Focus">{settings.focus}</SettingRow>
            </tbody>
          </table>

          {typeof window !== "undefined" && slug && (
            <div className="settings-qr-panel">
              <QRCodeSVG value={`${window.location.origin}/view/${slug}`} size={180} includeMargin level="M" />
              <p className="settings-qr-caption">Scan to pull this up on your phone on set</p>
            </div>
          )}
        </div>
        <MeasurePrompt recipe={recipe} lighting={lighting} camera={camera} />
        <NdGear camera={camera} settings={settings} />
      </section>

      <section aria-labelledby="why-heading">
        <h2 id="why-heading">Why These Settings</h2>
        {explanation.whyBlocks.map((block, i) => (
          <div className="why-block" key={i}>
            <h3>{block.title}</h3>
            <p>{block.text}</p>
          </div>
        ))}
      </section>

      {skinTone.relevant && (
        <section aria-labelledby="skin-tone-heading">
          <h2 id="skin-tone-heading">Skin tone</h2>
          <p className="why-block-lead">{skinTone.summary}</p>
          <div className="why-block">
            <h3>White balance and warmth</h3>
            <p>{skinTone.warmth}</p>
          </div>
          <div className="why-block">
            <h3>Sharpness</h3>
            <p>{skinTone.sharpness}</p>
          </div>
          <div className="why-block">
            <h3>Picture profile</h3>
            <p>{skinTone.profile}</p>
          </div>
        </section>
      )}

      {depth && depth.merged && (
        <section className="merged-covers" aria-labelledby="covers-heading">
          <h2 id="covers-heading">This page covers</h2>
          <ul className="covers-list">
            <li><strong>{scenario.name} in {lighting.name}</strong></li>
            {depth.merged.covered.map((c, i) => <li key={i}>{c.charAt(0).toUpperCase() + c.slice(1)}</li>)}
          </ul>
          <p className="covers-note">
            All of these produce the same recommendation on the {camera.name}. See below for why.
          </p>
        </section>
      )}

      {depth && depth.humanNotes.length > 0 && (
        <section className="depth-human" aria-labelledby="notes-heading">
          <h2 id="notes-heading">From shooting this</h2>
          {depth.humanNotes.map((n, i) => (
            <div className="depth-block" key={i}>
              <h3>{n.title}</h3>
              <p>{n.text}</p>
            </div>
          ))}
        </section>
      )}

      {depth && depth.sections.length > 0 && (
        <section aria-labelledby="depth-heading">
          <h2 id="depth-heading">What's specific to this setup</h2>
          {depth.sections.map((b, i) => (
            <div className="depth-block" key={i}>
              <h3>{b.title}</h3>
              <p>{b.text}</p>
            </div>
          ))}
        </section>
      )}

      {depth && depth.mistakes.length > 0 && (
        <section aria-labelledby="mistakes-heading">
          <h2 id="mistakes-heading">What usually goes wrong</h2>
          <ul className="depth-mistakes">
            {depth.mistakes.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </section>
      )}

      <section aria-labelledby="conditions-heading">
        <h2 id="conditions-heading">If Conditions Change</h2>
        <table className="conditions-table">
          <tbody>
            {explanation.conditionalBranches.map((b, i) => (
              <tr key={i}>
                <th>{b.condition}</th>
                <td>{b.change}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section aria-labelledby="camera-notes-heading">
        <h2 id="camera-notes-heading">About the {camera.name}</h2>
        <ul className="camera-notes">
          <li>Supported frame rates: {frameRateSummary(camera)}</li>
          <li>Aperture: {apertureSummary(camera)}</li>
          <li>Supported profiles: {profileSummary(camera)}</li>
          <li>
            ND filter support: {ndSummary(camera)}
            {camera.capabilities.ndNote ? `, ${camera.capabilities.ndNote}` : ""}
          </li>
          <li>
            Stabilization: {stabilizationSummary(camera)}
            {camera.capabilities.stabilizationNote ? `, ${camera.capabilities.stabilizationNote}` : ""}
          </li>
        </ul>
        {coverageNote(camera) && <p className="camera-coverage-note">{coverageNote(camera)}</p>}
      </section>

      <section aria-labelledby="editor-heading">
        <h2 id="editor-heading">Setting up the edit</h2>
        <p className="editor-lead">{delivery.editorSetup.lead}</p>
        <p className="editor-space">
          <span className="eyebrow">Input colour space</span>
          <span className="data">{delivery.editorSetup.space}</span>
        </p>
        <dl className="editor-list">
          {delivery.editorSetup.editors.map((e) => (
            <div className="editor-item" key={e.name}>
              <dt>{e.name}</dt>
              <dd>{e.body}</dd>
            </div>
          ))}
        </dl>
        {delivery.editorSetup.note && <p className="editor-note">{delivery.editorSetup.note}</p>}
      </section>

      <section className="delivery" aria-labelledby="delivery-heading">
        <h2 id="delivery-heading">Getting it to {output ? output.name : "the edit"}</h2>
        <ol className="delivery-steps">
          {delivery.steps.map((step, i) => (
            <li key={i}>
              <strong>{step.title}</strong>
              <p>{step.body}</p>
            </li>
          ))}
        </ol>
        <p className="delivery-note">{delivery.numbersNote}</p>
      </section>

      <Faq items={explanation.faq} name="recipe-faq" />

      {related.length > 0 && (
        <section aria-labelledby="related-heading">
          <h2 id="related-heading">Related Recipes</h2>
          <ul className="related-list">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to={`/camera/${r.slug}`}>{r.title}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {slug && (
        <section className="bonus-teaser" aria-labelledby="bonus-heading">
          <h2 id="bonus-heading">Want more?</h2>
          <p>
            This recipe also has a recommended SD card speed class{" "}
            {recipe.bonus?.panning ? "and panning guidance for this shot " : ""}
            available behind our free access code.{" "}
            <Link to={`/view/${slug}`}>Unlock the bonus tips →</Link>
          </p>
        </section>
      )}

      {/* Marker element read by scripts/prerender.js (Puppeteer) to know the
          page has finished computing + rendering its content before capturing HTML. */}
      <div data-prerender-ready="true" style={{ display: "none" }} />
    </article>
    </SplitPanel>
    <Footer />
    </>
  );
}
